import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link as MuiLink,
  Stack
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import WithPoolModal from '../CandidateSourcing/WithPoolModal';
import { useCandidatePoolModal } from '../CandidateSourcing/hooks/useCandidatePoolModal';
import PoolItem from '../CandidateSourcing/PoolItem';
import { ICandidateGroup } from './types';
import SourceIconLink from '../Components/CustomUIElements/SourceIconLink';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5)
}));

export default function CandidateGroup({ group }: { group: ICandidateGroup }) {
  const candidatePoolModalHook = useCandidatePoolModal();
  const { handlePoolClick } = candidatePoolModalHook;

  const totalCandidates = group.candidates.length;
  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <WithPoolModal candidatePoolModalHook={candidatePoolModalHook}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
          {group.name}
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <StyledCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" gutterBottom>
                    Information
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box display="flex" alignItems="center">
                      <WorkIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body1">{group.position || '-'}</Typography>
                    </Box>
                  </Grid>
                  {group.location && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box display="flex" alignItems="center">
                        <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body1">{group.location}</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </StyledCard>

            <StyledCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" gutterBottom>
                    Sources ({totalCandidates})
                  </Typography>
                </Box>
                <List dense>
                  {group.candidates.map((candidate, index) => (
                    <ListItem key={index} divider>
                      <ListItemIcon>
                        <SourceIconLink source={candidate.source} url={candidate.url} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <MuiLink
                            href={candidate.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            {candidate.id} - {candidate.name}
                          </MuiLink>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </StyledCard>

            <StyledCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                </Box>
                <Stack direction="column">
                  {group.emails.length > 0 && (
                    <Box display="flex" gap={0.5}>
                      <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {group.emails.map((email, index) => (
                        <StyledChip
                          key={index}
                          label={email}
                          size="small"
                          variant="outlined"
                          color="success"
                        />
                      ))}
                    </Box>
                  )}
                  {group.phones.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {group.phones.map((phone, index) => (
                        <StyledChip
                          key={index}
                          label={phone}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      ))}
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <StyledCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" gutterBottom>
                    Candidate Pools ({group.candidate_pools.length})
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {group.candidate_pools.map((pool) => (
                    <PoolItem
                      key={pool.id}
                      pool={pool}
                      onViewPool={() => handlePoolClick(pool.id)}
                    />
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </WithPoolModal>
    </Box>
  );
}
